import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

/**
 * One screen's (or one tab's) edit mode: everything on it is in edit mode or none of it is, and
 * a single GratiEditFab turns it on and off. Changes save as they're made, so leaving edit mode
 * ("Done") never saves anything — it only stops editing. Mount one provider per tab, inside the
 * tab's panel, so leaving the tab ends its edit mode.
 */
export interface EditModeContextValue {
  /** Whether the viewer may edit here at all (admins). Without it there is no edit FAB. */
  canEdit: boolean;
  isEditing: boolean;
  startEdit: () => void;
  stopEdit: () => void;
  /** Names what is being edited, for the FAB's accessible name: "Edit tee sheet". */
  label: string;
}

const EditModeContext = createContext<EditModeContextValue>({
  canEdit: false,
  isEditing: false,
  startEdit: () => {},
  stopEdit: () => {},
  label: '',
});

interface EditModeProviderProps {
  canEdit: boolean;
  /** What is being edited, lower case: "tee sheet". */
  label: string;
  children: ReactNode;
}

export function EditModeProvider({ canEdit, label, children }: EditModeProviderProps): ReactElement {
  const [isEditing, setIsEditing] = useState(false);
  const startEdit = useCallback(() => setIsEditing(true), []);
  const stopEdit = useCallback(() => setIsEditing(false), []);
  const value = useMemo(
    () => ({ canEdit, isEditing: canEdit && isEditing, startEdit, stopEdit, label }),
    [canEdit, isEditing, startEdit, stopEdit, label],
  );
  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
}

export function useEditMode(): EditModeContextValue {
  return useContext(EditModeContext);
}
